import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import { arrayOf, func, number, shape, string } from 'prop-types';
import React from 'react';
import Select from 'react-select';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledCollapse,
} from 'reactstrap';

import createModalToggle from '../../generic/create-modal-toggle';
import { creativeShape } from '../../prop-types';
import CompatibleCreatives from './CompatibleCreatives';
import IncompatibleCreatives from './IncompatibleCreatives';
import RequirementsGuide from './RequirementsGuide';

const isCompatible = ({ products }, id) =>
  products.compatibleProducts.some(product => product.id === id) ||
  products.noRequirementsProducts.some(product => product.id === id);

class CreativeAssociations extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCampaignLines: null,
      selectedCreatives: [],
    };
    this.onCampaignLineChange = this.onCampaignLineChange.bind(this);
    this.assignCreatives = this.assignCreatives.bind(this);
    this.onCreativeSelectionChange = this.onCreativeSelectionChange.bind(this);
  }

  componentDidMount() {
    const { lines } = this.props;
    if (lines.length === 1) {
      this.setState({
        selectedCampaignLines: [lines[0]],
      });
    }
  }

  onCampaignLineChange(options) {
    this.setState({
      selectedCampaignLines: options,
    });
  }

  onCreativeSelectionChange(e) {
    const { selectedCreatives } = e.target;
    this.setState({ selectedCreatives });
  }

  assignCreatives() {
    const { selectedCampaignLines, selectedCreatives } = this.state;

    this.props
      .assignCreatives(selectedCampaignLines, selectedCreatives)
      .then(this.props.toggle);
  }

  render() {
    const { selectedCampaignLines, selectedCreatives } = this.state;

    const { lines, model, productId, toggle } = this.props;
    const [compatibleCreatives, incompatibleCreatives] = partition(
      model,
      creative => isCompatible(creative, productId),
    );

    const noCreatives = isEmpty(compatibleCreatives);
    const noSelections = !selectedCampaignLines || !selectedCreatives.length; // selectedCampaignLines is null when no line is selected

    const countIncompatibles = incompatibleCreatives.length;
    return (
      <>
        <ModalHeader toggle={toggle}>
          Select Creatives for CampaignLine(s)
        </ModalHeader>
        <ModalBody>
          <div className="mb-4">
            {lines.length > 1 ? (
              <Select
                isMulti
                options={lines}
                value={selectedCampaignLines}
                onChange={this.onCampaignLineChange}
              />
            ) : (
              <h4 id="qa-default-campaign-line">{lines[0].label}</h4>
            )}
          </div>
          <div className="mb-4">
            <h5 className="h6">
              Compatible Creatives ({compatibleCreatives.length})
            </h5>
            <CompatibleCreatives
              creatives={compatibleCreatives}
              onChange={this.onCreativeSelectionChange}
              productId={productId}
            />
          </div>
          <div className="mb-4">
            <h5
              id="incompatibles"
              className={`h6 ${countIncompatibles ? 'dropdown-toggle' : ''}`}
            >
              Incompatible Creatives ({countIncompatibles})
            </h5>
            {!!countIncompatibles && (
              <UncontrolledCollapse toggler="incompatibles">
                <IncompatibleCreatives creatives={incompatibleCreatives} />
              </UncontrolledCollapse>
            )}
          </div>
          <RequirementsGuide productId={productId} />
          <div className="mb-4" />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={this.assignCreatives}
            disabled={noCreatives || noSelections}
            id="assign"
          >
            Assign
          </Button>
        </ModalFooter>
      </>
    );
  }
}

CreativeAssociations.propTypes = {
  productId: number.isRequired,
  model: arrayOf(creativeShape.isRequired),
  lines: arrayOf(
    shape({
      label: string,
      value: number.isRequired,
    }).isRequired,
  ).isRequired,
  assignCreatives: func.isRequired,
  toggle: func.isRequired,
};

CreativeAssociations.defaultProps = {
  model: [],
};

export default createModalToggle(
  'Assign Creatives',
  CreativeAssociations,
  { color: 'link', className: 'p-0' },
  { size: 'xl' },
);

export { CreativeAssociations };
